import { defineStore } from "pinia";
import {
  SubscriptionModule,
  SubscriptionType,
  type TicketGeneratingSubscriptionModule,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import type { DataValue } from "@/types.ts";
import { StoreModule } from "@/constants/store.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import {
  getMspDashboardRangeFrom,
  MspDashboardRange,
  MspUsersAndDevicesTrendExportOption,
} from "@/constants/msp.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions.ts";
import { type BackupAndRestoreWidgetData } from "@/constants/cloud-backup.ts";
import { RouteName } from "@/constants/routes.ts";
import router from "@/_helpers/router.ts";
import isEqual from "lodash/isEqual";
import { isModuleDisabled } from "@/_helpers/subscription.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export interface GeneratedTicketsStats {
  totalGenerated: number;
  openCount: number;
  closedCount: number;
}

export type ModuleTicketsStats = {
  [moduleName in TicketGeneratingSubscriptionModule]: number;
};

export interface WorkspaceTicket {
  workspace: {
    name: string;
    type: WorkspaceType;
    workspaceId: string;
  };
  ticketsCount: number;
}

export interface UsersStats {
  total: number;
  suspendedCount: number;
  withIssuesCount: number;
  noIssuesCount: number;
}

export interface DevicesStats {
  total: number;
  isolatedCount: number;
  withIssuesCount: number;
  noIssuesCount: number;
}

export interface UsersAndDevicesTrend {
  devicesDelta: number;
  usersDelta: number;
  items: UsersAndDevicesTrendChartItem[];
}

export interface UsersAndDevicesTrendChartItem {
  devices: number;
  users: number;
  date: number;
}

export interface UsersAndDevicesTrendExportData {
  fromTimestamp: number;
  exportFields: MspUsersAndDevicesTrendExportOption;
}

export interface WorkspacesStats {
  total: number;
  subscriptionCount: number;
  inactiveCount: number;
  trialCount: number;
  newCount: number;
  nearExpirationCount?: number;
}

export interface WorkspaceDeltaItem {
  workspaceId: string;
  workspaceName: string;
  delta: number;
}

export interface MspDashboardState {
  selectedSubscriptionPlans: SubscriptionType[];
  generatedTickets: DataValue<GeneratedTicketsStats>;
  moduleTickets: DataValue<ModuleTicketsStats>;
  workspaceTickets: DataValue<WorkspaceTicket[]>;
  usersStats: DataValue<UsersStats>;
  devicesStats: DataValue<DevicesStats>;
  workspacesStats: DataValue<WorkspacesStats>;
  topUsersDelta: DataValue<WorkspaceDeltaItem[]>;
  topDevicesDelta: DataValue<WorkspaceDeltaItem[]>;
  usersAndDevicesTrend: DataValue<UsersAndDevicesTrend>;
  backupAndRestore: DataValue<BackupAndRestoreWidgetData>;
  // Remembered so refetches not triggered by the widget (e.g. the subscription toggle) keep its range
  topUsersDeltaRange: MspDashboardRange;
  topDevicesDeltaRange: MspDashboardRange;
  usersAndDevicesTrendRange: MspDashboardRange;
}

export const useMspDashboardStore = defineStore(StoreModule.MSP_DASHBOARD, {
  state: (): MspDashboardState => ({
    selectedSubscriptionPlans: [SubscriptionType.SUBSCRIPTION, SubscriptionType.TRIAL],
    generatedTickets: { loading: true },
    moduleTickets: { loading: true },
    workspaceTickets: { loading: true },
    usersStats: { loading: true },
    devicesStats: { loading: true },
    workspacesStats: { loading: true },
    topUsersDelta: { loading: true },
    topDevicesDelta: { loading: true },
    usersAndDevicesTrend: { loading: true },
    backupAndRestore: { loading: true },
    topUsersDeltaRange: MspDashboardRange.LAST_7_DAYS,
    topDevicesDeltaRange: MspDashboardRange.LAST_7_DAYS,
    usersAndDevicesTrendRange: MspDashboardRange.LAST_7_DAYS,
  }),

  getters: {
    isSubscriptionOnlyFilter: (state) => {
      return isEqual(state.selectedSubscriptionPlans, [SubscriptionType.SUBSCRIPTION]);
    },
  },

  actions: {
    setSubscriptionFilter(value: boolean) {
      this.selectedSubscriptionPlans = value
        ? [SubscriptionType.SUBSCRIPTION]
        : [SubscriptionType.SUBSCRIPTION, SubscriptionType.TRIAL];
    },
    async fetchGeneratedTickets() {
      try {
        this.generatedTickets = { loading: true };
        const request = {
          ...api.mspDashboardGeneratedTickets(),
          params: {
            subscriptionTypes: this.selectedSubscriptionPlans.join(","),
          },
        };
        const { data } = await axiosInstance.request<GeneratedTicketsStats>(request);
        this.generatedTickets.data = data;
      } catch (error) {
        this.generatedTickets.error = true;
        console.error("Error fetching generated tickets:", error);
      } finally {
        this.generatedTickets.loading = false;
      }
    },

    async fetchModuleTickets() {
      try {
        this.moduleTickets = { loading: true };
        const request = {
          ...api.mspDashboardModuleTickets(),
          params: {
            subscriptionTypes: this.selectedSubscriptionPlans.join(","),
          },
        };
        const { data } = await axiosInstance.request<{
          ticketsPerModule: {
            [moduleName in TicketGeneratingSubscriptionModule]: number;
          };
        }>(request);
        this.moduleTickets.data = data.ticketsPerModule;
      } catch (error) {
        this.moduleTickets.error = true;
        console.error("Error fetching module tickets:", error);
      } finally {
        this.moduleTickets.loading = false;
      }
    },

    async fetchWorkspaceTickets() {
      try {
        this.workspaceTickets = { loading: true };

        const request = {
          ...api.mspDashboardWorkspaceTickets(),
          params: {
            subscriptionTypes: this.selectedSubscriptionPlans.join(","),
          },
        };
        const { data } = await axiosInstance.request<{ ticketsPerWorkspace: WorkspaceTicket[] }>(
          request
        );
        this.workspaceTickets.data = data.ticketsPerWorkspace;
      } catch (error) {
        this.workspaceTickets.error = true;
        console.error("Error fetching workspace tickets:", error);
      } finally {
        this.workspaceTickets.loading = false;
      }
    },

    async fetchUsersStats() {
      try {
        this.usersStats = { loading: true };
        const request = {
          ...api.mspDashboardUsersStats(),
          params: {
            subscriptionTypes: this.selectedSubscriptionPlans.join(","),
          },
        };
        const { data } = await axiosInstance.request<UsersStats>(request);
        this.usersStats.data = data;
      } catch (error) {
        this.usersStats.error = true;
        console.error("Error fetching users stats:", error);
      } finally {
        this.usersStats.loading = false;
      }
    },

    async fetchDevicesStats() {
      try {
        this.devicesStats = { loading: true };
        const request = {
          ...api.mspDashboardDevicesStats(),
          params: {
            subscriptionTypes: this.selectedSubscriptionPlans.join(","),
          },
        };
        const { data } = await axiosInstance.request<DevicesStats>(request);
        this.devicesStats.data = data;
      } catch (error) {
        this.devicesStats.error = true;
        console.error("Error fetching devices stats:", error);
      } finally {
        this.devicesStats.loading = false;
      }
    },

    async fetchWorkspacesStats() {
      try {
        this.workspacesStats = { loading: true };
        // This request configuration intentionally doesn't use `subscriptionTypes` param as it doesn't make any sense.
        // This widget displays information regarding all available workspaces subscription types regardless of filter.
        const request = {
          ...api.mspDashboardWorkspacesStats(),
        };
        const { data } = await axiosInstance.request<WorkspacesStats>(request);
        this.workspacesStats.data = data;
      } catch (error) {
        this.workspacesStats.error = true;
        console.error("Error fetching workspaces stats:", error);
      } finally {
        this.workspacesStats.loading = false;
      }
    },

    async fetchTopUsersDelta(range?: MspDashboardRange) {
      if (range) {
        this.topUsersDeltaRange = range;
      }
      try {
        const from = getMspDashboardRangeFrom(this.topUsersDeltaRange);
        this.topUsersDelta = { loading: true };
        const request = {
          ...api.mspDashboardTopUsersDelta(from, this.selectedSubscriptionPlans.join(",")),
        };
        const { data } = await axiosInstance.request<{ items: WorkspaceDeltaItem[] }>(request);
        this.topUsersDelta.data = data.items;
      } catch (error) {
        this.topUsersDelta.error = true;
        console.error("Error fetching top users delta:", error);
      } finally {
        this.topUsersDelta.loading = false;
      }
    },

    async fetchTopDevicesDelta(range?: MspDashboardRange) {
      if (range) {
        this.topDevicesDeltaRange = range;
      }
      try {
        const from = getMspDashboardRangeFrom(this.topDevicesDeltaRange);
        this.topDevicesDelta = { loading: true };
        const request = {
          ...api.mspDashboardTopDevicesDelta(from, this.selectedSubscriptionPlans.join(",")),
        };
        const { data } = await axiosInstance.request<{ items: WorkspaceDeltaItem[] }>(request);
        this.topDevicesDelta.data = data.items;
      } catch (error) {
        this.topDevicesDelta.error = true;
        console.error("Error fetching top devices delta:", error);
      } finally {
        this.topDevicesDelta.loading = false;
      }
    },

    async fetchUsersAndDevicesTrend(range?: MspDashboardRange) {
      if (range) {
        this.usersAndDevicesTrendRange = range;
      }
      try {
        const from = getMspDashboardRangeFrom(this.usersAndDevicesTrendRange);
        this.usersAndDevicesTrend = { loading: true };
        const request = {
          ...api.mspUsersAndDevicesTrend(from, this.selectedSubscriptionPlans.join(",")),
        };
        const { data } = await axiosInstance.request<UsersAndDevicesTrend>(request);
        this.usersAndDevicesTrend.data = data;
      } catch (error) {
        this.usersAndDevicesTrend.error = true;
        console.error("Error fetching top devices delta:", error);
      } finally {
        this.usersAndDevicesTrend.loading = false;
      }
    },

    async exportUsersAndDevicesTrend(payload: UsersAndDevicesTrendExportData) {
      try {
        const permissionStore = usePermissionsStore();
        const snackbarStore = useSnackbarStore();
        const request = {
          ...api.exportMspUsersAndDevicesTrend({
            ...payload,
            subscriptionTypes: this.selectedSubscriptionPlans,
          }),
        };
        await axiosInstance.request<UsersAndDevicesTrend>(request);
        const isGlobalContext = permissionStore.isGlobalAdmin;

        const targetRoute = isGlobalContext ? RouteName.ACTIVITY_LOGS : RouteName.MSP_REPORTS;

        const pageName = isGlobalContext
          ? i18n.global.t("activityLogs.title")
          : i18n.global.t("reports.title");

        const message = i18n.global.t("snackbar.messages.general.exportCsvStarted", { pageName });

        if (isGlobalContext) {
          snackbarStore.addGenericSuccess(message);
        } else {
          snackbarStore.addSuccessWithCallbackBtn({
            html: message,
            callback: async () => await router.push({ name: targetRoute }),
            callbackName: i18n.global.t("general.view"),
          });
        }
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Fetches data for the Cloud Backup (Backup & Restore) widget.
     */
    async fetchBackupAndRestore() {
      try {
        this.backupAndRestore = { loading: true };
        const { data } = await axiosInstance.request<BackupAndRestoreWidgetData>({
          ...api.mspBackupAndRestoreDashboard(),
        });
        this.backupAndRestore.data = data;
      } catch (error) {
        this.backupAndRestore.error = true;
        console.error("Error fetching Backup and Restore data:", error);
      } finally {
        this.backupAndRestore.loading = false;
      }
    },

    async getAllWidgetsData() {
      try {
        const cloudBackupRestricted =
          isMspAccessRestricted(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_BACKUP_AND_RESTORE
          ) || isModuleDisabled(SubscriptionModule.BACKUP_AND_RESTORE);

        await Promise.all([
          this.fetchGeneratedTickets(),
          this.fetchModuleTickets(),
          this.fetchWorkspaceTickets(),
          this.fetchUsersStats(),
          this.fetchDevicesStats(),
          this.fetchWorkspacesStats(),
          this.fetchTopUsersDelta(),
          this.fetchTopDevicesDelta(),
          this.fetchUsersAndDevicesTrend(),
          ...(cloudBackupRestricted ? [] : [this.fetchBackupAndRestore()]),
        ]);
      } catch (error) {
        console.error("Error fetching all dashboard data:", error);
      }
    },
  },
});
